import React, { useState } from "react";
import Modal from "../../../../Common/Modal";
import MtiButton from "../../../../Buttons/MtiButton";
import { ECarrier } from "../../../../../Model/ECarrier";
import CalibrationApi from "../../../../../api/CalibrationApi";
import { EModuleType } from "../../../../../Model/EModuleType";
import CheckBox from "../../../../Buttons/CheckBox";
import "./RunAutoCalibrationModal.css";
import { useTypedSelector } from "../../../../../redux/reducers/RootReducer";
import { getModuleState } from "../../../../../redux/reducers/ModuleStateReducer";
import { getModules } from "../../../../../redux/reducers/ModuleReducer";
import { CarrierState, EPsrState } from "../../../../../Model/ModuleState";
import AutoCalibrationUtil from "../../../../../Utility/AutoCalibrationUtil";

type CarrierIconDisplayValues = {
  icon: React.ReactNode;
  display: string;
  value: ECarrier;
};

const hotelCarrierIcon = (
  <svg
    width="16"
    height="14"
    viewBox="0 0 16 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.914307 7.72483L2.1466 6.49254H10.1565V13.8863H0.914307V7.72483ZM8.92423 7.72483H7.69194V8.95713H8.92423V7.72483ZM7.07579 7.72483H5.84349V8.95713H7.07579V7.72483ZM5.22734 7.72483H3.99505V8.95713H5.22734V7.72483ZM3.3789 7.72483H2.1466V8.95713H3.3789V7.72483ZM7.69194 9.57328H8.92423V10.8056H7.69194V9.57328ZM7.69194 11.4217H8.92423V12.654H7.69194V11.4217ZM2.1466 12.654H3.3789V11.4217H2.1466V12.654ZM3.99505 12.654H5.22734V11.4217H3.99505V12.654ZM5.84349 12.654H7.07579V11.4217H5.84349V12.654ZM7.07579 9.57328V10.8056H5.84349V9.57328H7.07579ZM5.22734 9.57328V10.8056H3.99505V9.57328H5.22734ZM3.3789 9.57328V10.8056H2.1466V9.57328H3.3789Z"
      fill="#414042"
    />
    <path
      d="M5.84349 1.56335L7.07579 0.331055H15.0857V7.72483H13.2373V6.49254H13.8534V5.26024H13.2373V4.64409H13.8534V3.4118H13.2373V2.79565H13.8534V1.56335H12.6211V2.79565H12.005V1.56335H10.7727V2.79565H10.1565V1.56335H8.92423V2.79565H8.30808V1.56335H7.07579V2.79565H5.84349V1.56335Z"
      fill="#414042"
    />
    <path
      d="M3.3789 4.64409L4.6112 3.4118H12.6211V10.8056H10.7727V9.57328H11.3888V8.34098H10.7727V7.72483H11.3888V6.49254H10.7727V5.87639H11.3888V4.64409H10.1565V5.87639H9.54038V4.64409H8.30808V5.87639H7.69194V4.64409H6.45964V5.87639H5.84349V4.64409H4.6112V5.87639H3.3789V4.64409Z"
      fill="#414042"
    />
  </svg>
);

const loadPortCarrierIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.914307 4.13508V6.06755H15.0857V4.13508H0.914307Z"
      fill="#414042"
    />
    <path
      d="M3.49093 11.2208L8.00001 6.7117L12.5091 11.2208H9.28832V15.0857H6.7117V11.2208H3.49093Z"
      fill="#414042"
    />
    <path
      d="M1.55846 3.49093H14.4416L11.8649 0.914307H4.13508L1.55846 3.49093Z"
      fill="#414042"
    />
  </svg>
);

const getHotelCarrierDisplayValues = (
  carrierStates: CarrierState[]
): CarrierIconDisplayValues[] => {
  let hotelCarrierDisplayValues: CarrierIconDisplayValues[] = [];
  let carriers =
    AutoCalibrationUtil.getAvailableCarrierForAutoCalibration(carrierStates);

  carriers.forEach((carrier) => {
    hotelCarrierDisplayValues.push({
      icon:
        carrier === ECarrier.LoadPort ? loadPortCarrierIcon : hotelCarrierIcon,
      display: AutoCalibrationUtil.getCarrierName(carrier),
      value: carrier,
    });
  });

  return hotelCarrierDisplayValues;
};

type Props = {
  show: boolean;
  onHide: () => void;
  onRunAutoCalibration: () => void;
};

export default function RunAutoCalibrationModal({
  show,
  onHide,
  onRunAutoCalibration,
}: Props) {
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrModules[0].identifier).psr
  );

  if (psrState == null) {
    throw new Error("Psr state is null");
  }

  const hotelCarrierDisplayValues = getHotelCarrierDisplayValues(
    psrState?.carrierStates
  );

  const [selectedCarriers, setSelectedCarriers] = useState(
    hotelCarrierDisplayValues.map((hc) => hc.value) as ECarrier[]
  );

  const updateSelectedCarriers = (key: ECarrier) => {
    if (selectedCarriers.includes(key)) {
      const newSelected = selectedCarriers.filter((carrier) => {
        return carrier !== key;
      });
      setSelectedCarriers(newSelected);
    } else {
      setSelectedCarriers([...selectedCarriers, key]);
    }
  };

  const updateAllSelectedCarriers = () => {
    if (
      selectedCarriers.length < hotelCarrierDisplayValues.length ||
      selectedCarriers.length === 0
    ) {
      setSelectedCarriers(
        hotelCarrierDisplayValues.map((carrier) => {
          return carrier.value;
        })
      );
    } else if (selectedCarriers.length === hotelCarrierDisplayValues.length) {
      setSelectedCarriers([]);
    }
  };

  const runAutoCalibration = () => {
    CalibrationApi.runStorageAutoCalibration(
      "SUITE",
      EModuleType.PSR,
      selectedCarriers
    ).then(() => {
      onRunAutoCalibration();
    });
  };

  return (
    <Modal
      show={show}
      className="auto-calibration-run-modal"
      onHide={onHide}
      hideOnBackdropClick={true}
    >
      <Modal.Header
        title={"Choose Carrier for Autocalibration"}
        hideButtonEnabled={false}
        className="auto-calibration-run-header"
      />
      <Modal.Body className="auto-calibration-run-body">
        <div className="auto-calibration-paragraph">
          <p>
            Please select carrier you want to calibrate using auto calibration
          </p>
        </div>
        <div className="auto-calibration-carriers">
          <div className="carriers-select-all">
            <CheckBox
              checked={
                hotelCarrierDisplayValues.length === selectedCarriers.length
              }
              id="auto-calibration-select-all"
              label="Select All"
              name="auto-calibration-select-all"
              colorVariant="alternate"
              onChange={updateAllSelectedCarriers}
            />
          </div>
          <div className="carrier-checkboxes">
            {hotelCarrierDisplayValues.map((dvp) => {
              return (
                <CheckBox
                  id={"auto-calibration-" + ECarrier[dvp.value]}
                  key={"auto-calibration-" + ECarrier[dvp.value]}
                  checked={selectedCarriers.includes(dvp.value)}
                  name="auto-calibration-select-all"
                  colorVariant="alternate"
                  onChange={() => updateSelectedCarriers(dvp.value)}
                >
                  {dvp.icon}
                  <span>{dvp.display}</span>
                </CheckBox>
              );
            })}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="auto-calibration-run-footer">
        <MtiButton
          variant="secondary"
          colorVariant="default"
          size="large"
          onClick={onHide}
          style={{
            width: 157,
          }}
        >
          <span>CANCEL</span>
        </MtiButton>
        <MtiButton
          disabled={selectedCarriers.length == 0 || psrState.state === EPsrState.Busy}
          variant="primary"
          colorVariant="default"
          size="large"
          onClick={runAutoCalibration}
          style={{
            width: 157,
          }}
        >
          <span>CALIBRATE</span>
        </MtiButton>
      </Modal.Footer>
    </Modal>
  );
}
